/**
 * Formats a postal / ZIP input by grouping digits with a separator (e.g. SK "234 56").
 * Strips non-digits, caps to the total digit count implied by `digitGroups`, then joins groups.
 * If the first group is complete and the user typed whitespace before the next digit, the
 * separator is kept (e.g. manual "123 " does not collapse to "123").
 *
 * @param {string} rawValue - Value as typed or pasted (may include spaces or other characters)
 * @param {Object} options
 * @param {number[]} options.digitGroups - Length of each digit block, in order (e.g. [3, 2] for SK)
 * @param {string} [options.separator=' '] - Separator inserted between full groups
 * @returns {string}
 */
export function formatPostalCodeInput(rawValue, options = {}) {
  const { digitGroups = [], separator = ' ' } = options
  if (!Array.isArray(digitGroups) || digitGroups.length === 0) {
    return rawValue ?? ''
  }
  const raw = String(rawValue ?? '')
  const digits = raw.replace(/\D/g, '')
  const maxDigits = digitGroups.reduce((sum, n) => sum + n, 0)
  const capped = digits.slice(0, maxDigits)
  const parts = []
  let offset = 0
  for (const len of digitGroups) {
    if (offset >= capped.length) {
      break
    }
    parts.push(capped.slice(offset, offset + len))
    offset += len
  }
  let result = parts.join(separator)

  // Keep separator after the first group when user typed a space before the second
  // (otherwise /\D/g strips it and "123 " snaps back to "123".)
  if (digitGroups.length >= 2 && capped.length > 0) {
    const firstLen = digitGroups[0]
    if (capped.length === firstLen && capped.length < maxDigits) {
      let digitCount = 0
      let endIdx = -1
      for (let i = 0; i < raw.length; i++) {
        if (/\d/.test(raw[i])) {
          digitCount += 1
          if (digitCount === firstLen) {
            endIdx = i
            break
          }
        }
      }
      if (endIdx !== -1 && /\s/.test(raw.slice(endIdx + 1))) {
        result = capped.slice(0, firstLen) + separator
      }
    }
  }

  return result
}

/**
 * Display max length for a formatted postal code (digits + separators between groups).
 *
 * @param {Object} options
 * @param {number[]} options.digitGroups
 * @param {string} [options.separator=' ']
 * @returns {number}
 */
export function getPostalCodeFormattedMaxLength(options = {}) {
  const { digitGroups = [], separator = ' ' } = options
  if (!digitGroups.length) {
    return 0
  }
  const digitCount = digitGroups.reduce((a, b) => a + b, 0)
  const sepLen = separator ? String(separator).length : 0
  return digitCount + Math.max(0, digitGroups.length - 1) * sepLen
}
